import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useOAuthWebApp } from './../API/Account';

import './../Assets/Css/index.css';

import BlockImg from './../Assets/Pngs/block.jpg';

import ShopRouter from './Shop/ShopRouter';

import { useShopAPI } from './../API/Shop';

import { useTranslation } from 'react-i18next';
import {Box, Card} from "@mui/material";
import { canUseCatalogBootstrap, catalogRequestSucceeded } from './Shop/catalogLoadState';
import PoweredByWebappz from "../Components/Cards/PoweredByWebappz";

const GoodDialog = React.lazy(() => import('./../Components/Dialogs/GoodDialog'));

const ShopBootstrapShell = () => (
    <Box className="shop-bootstrap-shell" aria-label="Загрузка магазина" />
);

const ProductBootstrapShell = ({ label }) => (
    <Box className="shop-product-bootstrap-shell">
        <div className="shop-product-bootstrap-topbar">
            <div className="shop-product-bootstrap-pill"></div>
            <div className="shop-product-bootstrap-circle"></div>
        </div>
        <div className="shop-product-bootstrap-image"></div>
        <div className="shop-product-bootstrap-body">
            <div className="shop-product-bootstrap-line title"></div>
            <div className="shop-product-bootstrap-line price"></div>
            <div className="shop-product-bootstrap-line"></div>
            <div className="shop-product-bootstrap-line short"></div>
            <div className="shop-product-bootstrap-button"></div>
            <p>{label}</p>
        </div>
    </Box>
);

const CartBootstrapShell = ({ label }) => (
    <Box className="shop-cart-bootstrap-shell">
        <div className="shop-cart-bootstrap-header">
            <div className="shop-cart-bootstrap-title"></div>
            <div className="shop-cart-bootstrap-action"></div>
        </div>
        <div className="shop-cart-bootstrap-list">
            {Array.from({ length: 3 }).map((_, index) => (
                <Card key={index} className="shop-cart-bootstrap-card">
                    <div className="shop-cart-bootstrap-image"></div>
                    <div className="shop-cart-bootstrap-content">
                        <div className="shop-cart-bootstrap-line title"></div>
                        <div className="shop-cart-bootstrap-line"></div>
                        <div className="shop-cart-bootstrap-line short"></div>
                    </div>
                </Card>
            ))}
        </div>
        <div className="shop-cart-bootstrap-total"></div>
        <p>{label}</p>
    </Box>
);

const getTelegramStartParam = () => {
    const telegramStartParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;

    if (typeof telegramStartParam === 'string' && telegramStartParam.length > 0) {
        return telegramStartParam;
    }

    return new URLSearchParams(window.location.search).get('tgWebAppStartParam') || undefined;
};

const parseItemStartParam = (startParam) => {
    if (typeof startParam !== 'string' || startParam.length < 5) {
        return false;
    }

    const itemParam = startParam.split('--').find(param => /^item\d+$/.test(param));
    if (!itemParam) {
        return false;
    }

    const match = itemParam.match(/^item(\d+)$/);
    return match ? match[1] : false;
};

const parseCartStartItems = (startParam) => {
    if (typeof startParam !== 'string' || startParam.length < 6) {
        return [];
    }

    const cartParam = startParam.split('--').find(param => /^cart_/.test(param));
    if (!cartParam) {
        return [];
    }

    const parsedItems = cartParam
        .replace(/^cart_/, '')
        .split('_')
        .map(param => {
            const match = param.match(/^i(\d+)h([0-9a-fA-F]+)(?:q(\d+))?$/);
            if (!match) {
                return null;
            }
            return {
                itemID: match[1],
                size: decodeCartSize(match[2]),
                count: parseCartCount(match[3]),
                hasExplicitCount: Boolean(match[3])
            };
        })
        .filter(Boolean);

    return normalizeCartStartItems(parsedItems);
};

const decodeCartSize = (hex) => {
    if (!hex || hex.length % 2 !== 0) {
        return '0';
    }

    try {
        const encoded = hex.match(/.{1,2}/g)
            .map(byte => `%${byte}`)
            .join('');
        return decodeURIComponent(encoded) || '0';
    } catch (error) {
        return '0';
    }
};

const parseCartCount = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return 1;
    }

    return Math.min(99, Math.floor(parsed));
};

const normalizeCartStartItems = (cartItems) => {
    const grouped = new Map();

    cartItems.forEach(cartItem => {
        const key = `${cartItem.itemID}:${cartItem.size || '0'}`;
        const current = grouped.get(key);
        const nextCount = cartItem.hasExplicitCount ? cartItem.count : 1;

        if (!current) {
            grouped.set(key, {
                itemID: cartItem.itemID,
                size: cartItem.size || '0',
                count: nextCount,
                hasExplicitCount: cartItem.hasExplicitCount
            });
            return;
        }

        if (cartItem.hasExplicitCount) {
            grouped.set(key, {
                ...current,
                count: Math.max(current.count || 1, nextCount),
                hasExplicitCount: true
            });
        }
    });

    return Array.from(grouped.values()).map(({ hasExplicitCount, ...cartItem }) => cartItem);
};

const ShopLayout = ({ view }) => {
    const UserSession = useSelector(state => state.user.user);
    const ShopSession = useSelector(state => state.shop.shop);

    const {t} = useTranslation();

    const [viewType, setViewType] = React.useState(view);

    const routeParams = useParams();
    const { shopID: routeShopID, parameter: routeParameter } = routeParams;

    const getInitialShopID = () => {
        if (routeShopID !== undefined && routeShopID !== null) {
            return routeShopID;
        }

        const startParam = getTelegramStartParam();
        if (startParam !== undefined) {
            const params = startParam.split('--');
            let foundShopID = false;

            params.some(param => {
                const match = param.match(/shopid(\w+)/);
                if (match) {
                    foundShopID = match[1];
                    return true;
                }
                return false;
            });

            if (foundShopID !== false) {
                return foundShopID;
            }
        }

        return 0;
    };

    const [shopid, setShopid] = React.useState(getInitialShopID);

    React.useEffect(() => {
        setShopid(getInitialShopID());
    }, [routeShopID]);

    const getInitialItemID = () => parseItemStartParam(getTelegramStartParam()) || parseItemStartParam(routeParameter);
    const initialItemID = getInitialItemID();
    const initialCartItems = parseCartStartItems(getTelegramStartParam());

    const [parameter, setParameter] = React.useState(routeParameter);

    const { getGoods, getCategories, getGood, getBasket, AddItemBasketByIDAction, SetBasketItemCountAction } = useShopAPI();
    const basketRequestRef = React.useRef(null);
    const getBasketRef = React.useRef(getBasket);
    getBasketRef.current = getBasket;

    const loadBasketOnce = React.useCallback(() => {
        if (!basketRequestRef.current) {
            basketRequestRef.current = Promise.resolve(getBasketRef.current()).catch(() => null);
        }

        return basketRequestRef.current;
    }, []);

    const [openBackdrop, setOpenBackdrop] = React.useState(false);
    const [isLoadingGoods, setIsLoadingGoods] = React.useState(false);
    const [checkedGoodsRequestKey, setCheckedGoodsRequestKey] = React.useState(null);
    const [preloader, setPreloader] = React.useState(true);
    const [openBasket, setOpenBasket] = React.useState(initialCartItems.length > 0);
    const [openCatalog, setOpenCatalog] = React.useState(false);
    const [openProfile, setOpenProfile] = React.useState(false);
    const [openInfoShop, setOpenInfoShop] = React.useState(false);

    const [categoryID, setCategoryID] = React.useState(0);
    const [offsetSearchGoods, setOffsetSearchGoods] = React.useState(0);
    const [filterSearchGoods, setFilterSearchGoods] = React.useState('0');
    const [catalogID, setCatalogID] = React.useState(0);
    const [textSearchGoods, setTextSearchGoods] = React.useState('');
    const [shopIDSearchGoods, setShopIDSearchGoods] = React.useState(shopid);
    const [containerTag, setContainerTag] = React.useState('');
    const [selectShop, setSelectShop] = React.useState('');
    const goodsRequestKey = JSON.stringify([
        categoryID,
        filterSearchGoods,
        textSearchGoods,
        String(shopIDSearchGoods),
        catalogID
    ]);
    const categoriesRequestKey = JSON.stringify([catalogID, String(shopIDSearchGoods)]);
    const hasCheckedGoods = checkedGoodsRequestKey === goodsRequestKey;
    const [loadedCategoriesRequestKey, setLoadedCategoriesRequestKey] = React.useState(null);
    const [item, setItem] = React.useState(false);
    const [requestedItemID, setRequestedItemID] = React.useState(initialItemID);
    const [itemOpenState, setItemOpenState] = React.useState(initialItemID !== false ? 'pending' : 'idle');
    const [requestedCartItems] = React.useState(initialCartItems);
    const [cartOpenState, setCartOpenState] = React.useState(initialCartItems.length > 0 ? 'ready' : 'idle');
    const [cartHydrationState, setCartHydrationState] = React.useState(initialCartItems.length > 0 ? 'loading' : 'idle');

    const navigate = useNavigate();

    const [openGoodModal, setOpenGoodModal] = React.useState(initialItemID !== false);
    const cartStartHydratedRef = React.useRef(false);
    const productStartOpenedRef = React.useRef(false);
    const catalogRequestSequenceRef = React.useRef(0);

    React.useEffect(() => {
        document.documentElement.dataset.webappzCatalogReady = 'false';

        return () => {
            delete document.documentElement.dataset.webappzCatalogReady;
        };
    }, [shopid]);

    React.useEffect(() => {
        if (openBasket || openCatalog || openProfile || openInfoShop) {

            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.documentElement.style.overflowY = 'hidden';

            return () => {
                document.documentElement.style.overflowY = 'scroll';
            };
        }
    }, [openBasket, openCatalog, openProfile, openInfoShop]);

    const modalHandlers = {
        handleOpenBackdrop: () => setOpenBackdrop(true),
        handleCloseBackdrop: () => setOpenBackdrop(false),
        handleCloseBasketDialog: () => setOpenBasket(false),
        handleOpenBasketDialog: () => setOpenBasket(true),
        handleOpenLoadingGoods: () => setIsLoadingGoods(true),
        handleCloseLoadingGoods: () => setIsLoadingGoods(false),
        handleChangeCategoryID: (categoryID) => setCategoryID(categoryID),
        openBackdrop: openBackdrop,
        isLoadingGoods: isLoadingGoods,
        hasCheckedGoods: hasCheckedGoods,
        preloader: preloader,
        openBasket: openBasket,
        openCatalog: openCatalog,
        setOpenCatalog: setOpenCatalog,
        openProfile: openProfile,
        openInfoShop: openInfoShop,
        setOpenInfoShop: setOpenInfoShop,
        keepBasketOpen: requestedCartItems.length > 0,
        cartHydrationState: cartHydrationState,
        parameter: parameter,
        setParameter: setParameter,
        handleCloseProfileDialog: () => {
            setOpenProfile(false);
            sessionStorage.setItem('profileClosed', 'true');
        },
        handleOpenProfileDialog: () => {
            setOpenProfile(true);
            sessionStorage.removeItem('profileClosed');
        },
        openGoodModal: openGoodModal,
        setOpenGoodModal: setOpenGoodModal,
        viewType: viewType,
        setViewType: setViewType,
        goodsParams: {
            categoryID: categoryID,
            catalogID: catalogID,
            offsetSearchGoods: offsetSearchGoods,
            filterSearchGoods: filterSearchGoods,
            textSearchGoods: textSearchGoods,
            shopIDSearchGoods: shopIDSearchGoods,
            containerTag: containerTag,
            selectShop: selectShop
        },
        setGoodsParams: {
            setCategoryID: setCategoryID,
            setOffsetSearchGoods: setOffsetSearchGoods,
            setFilterSearchGoods: setFilterSearchGoods,
            setCatalogID: setCatalogID,
            setTextSearchGoods: setTextSearchGoods,
            setShopIDSearchGoods: setShopIDSearchGoods,
            setContainerTag: setContainerTag,
            setSelectShop: setSelectShop
        }
    };

    React.useEffect(() => {
        if (UserSession === null || ShopSession.settings === undefined) {
            return;
        }

        const bootstrap = window.__WEBAPPZ_CATALOG_BOOTSTRAP__;
        if (canUseCatalogBootstrap(bootstrap, shopIDSearchGoods, goodsRequestKey, checkedGoodsRequestKey)) {
            setCheckedGoodsRequestKey(goodsRequestKey);
            setIsLoadingGoods(false);
            return;
        }

        if (hasCheckedGoods) {
            setIsLoadingGoods(false);
            return;
        }

        if (itemOpenState === 'pending' || cartOpenState === 'pending') {
            setIsLoadingGoods(false);
            return;
        }

        const abortCtrl = new AbortController();
        const requestSequence = ++catalogRequestSequenceRef.current;

        const fetchData = async () => {
            setIsLoadingGoods(true);
            setOffsetSearchGoods(0);

            const paramsSearch = {
                categoryID: categoryID,
                offset: 0,
                filter: filterSearchGoods,
                searchText: textSearchGoods,
                catalogID: catalogID,
                shopid: shopIDSearchGoods,
                append: false
            };

            const result = await getGoods(paramsSearch, abortCtrl, modalHandlers.handleCloseLoadingGoods);

            if (
                !abortCtrl.signal.aborted
                && requestSequence === catalogRequestSequenceRef.current
                && catalogRequestSucceeded(result)
            ) {
                setCheckedGoodsRequestKey(goodsRequestKey);
            }
            setOpenBackdrop(false);
            modalHandlers.handleCloseBackdrop();
        };

        fetchData();

        return () => {
            abortCtrl.abort();
        };
    }, [
        UserSession,
        ShopSession.settings?.show,
        categoryID,
        filterSearchGoods,
        textSearchGoods,
        shopIDSearchGoods,
        catalogID,
        itemOpenState,
        cartOpenState,
        goodsRequestKey,
        hasCheckedGoods
    ]);

    React.useEffect(() => {
        const bootstrap = window.__WEBAPPZ_CATALOG_BOOTSTRAP__;
        if (
            UserSession === null
            || ShopSession.settings === undefined
            || ShopSession.settings.show !== undefined
            || loadedCategoriesRequestKey === categoriesRequestKey
        ) {
            return undefined;
        }

        if (
            bootstrap?.categories === true
            && bootstrap.shopid === String(shopIDSearchGoods)
            && categoriesRequestKey === JSON.stringify([0, String(shopIDSearchGoods)])
        ) {
            setLoadedCategoriesRequestKey(categoriesRequestKey);
            return undefined;
        }

        let cancelled = false;
        const fetchCategories = async () => {
            const result = await getCategories(catalogID, shopIDSearchGoods);
            if (!cancelled && result?.data?.status === 'success') {
                setLoadedCategoriesRequestKey(categoriesRequestKey);
            }
        };

        fetchCategories();
        return () => {
            cancelled = true;
        };
    }, [
        UserSession,
        ShopSession.settings?.show,
        catalogID,
        shopIDSearchGoods,
        categoriesRequestKey,
        loadedCategoriesRequestKey
    ]);

    React.useEffect(() => {
        if (!hasCheckedGoods || UserSession === null) {
            return undefined;
        }

        let idleID = null;
        let timeoutID = null;
        const afterPaint = window.requestAnimationFrame(() => {
            document.documentElement.dataset.webappzCatalogReady = 'true';
            window.dispatchEvent(new Event('webappz:catalog-ready'));

            if (requestedCartItems.length > 0) {
                return;
            }

            if (typeof window.requestIdleCallback === 'function') {
                idleID = window.requestIdleCallback(loadBasketOnce, { timeout: 2500 });
            } else {
                timeoutID = window.setTimeout(loadBasketOnce, 1200);
            }
        });

        return () => {
            window.cancelAnimationFrame(afterPaint);
            if (idleID !== null && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(idleID);
            }
            if (timeoutID !== null) {
                window.clearTimeout(timeoutID);
            }
        };
    }, [hasCheckedGoods, UserSession, requestedCartItems, loadBasketOnce]);

    React.useEffect(() => {
        if (openBasket && UserSession !== null && requestedCartItems.length < 1) {
            loadBasketOnce();
        }
    }, [openBasket, UserSession, requestedCartItems, loadBasketOnce]);

    const { OAuthWebApp } = useOAuthWebApp();

    const [errorAuth, setErrorAuth] = useState(false);

    React.useEffect(() => {
        const fetchData = async () => {
            const WebApp = window.Telegram.WebApp;

            if (typeof (WebApp.initDataUnsafe.user) !== 'undefined') {
                var user = {
                    userid: `${WebApp.initDataUnsafe.user.id}`,
                    first_name: `${WebApp.initDataUnsafe.user.first_name}`,
                    last_name: `${WebApp.initDataUnsafe.user.last_name}`,
                    username: `${WebApp.initDataUnsafe.user.username}`,
                    token: "token",
                    shopid: shopid,
                };

                const startParam = getTelegramStartParam();

                if(startParam !== undefined) {
                    const params = startParam.split('--');

                    var inviteAdminToken = false;

                    params.some(param => {
                        const match = param.match(/sa_(\w+)/);
                        if (match) {
                            inviteAdminToken = match[1];
                            user.inviteAdminToken = inviteAdminToken
                            return true;
                        }
                        return false;
                    });
                }

                try {
                    const result = await OAuthWebApp(user, setShopIDSearchGoods);

                    if(result?.status !== undefined && result.status === 'error') {
                        setErrorAuth(result.message);
                    }

                    if (result?.bootstrap?.goods?.status === 'success') {
                        const bootstrapGoodsKey = JSON.stringify([0, '0', '', String(result.shopid), 0]);
                        setCheckedGoodsRequestKey(bootstrapGoodsKey);
                    }

                    if (result?.bootstrap?.categories?.status === 'success') {
                        const bootstrapCategoriesKey = JSON.stringify([0, String(result.shopid)]);
                        setLoadedCategoriesRequestKey(bootstrapCategoriesKey);
                    }

                } catch (error) {
                    console.error("Error with Authorization");
                }
            } else {
                window.location.href = '/block';
            }
        }

        fetchData();
    }, []);

    // window.addEventListener('beforeunload', () => {
    //     const orderOpenID = sessionStorage.getItem('orderOpenID');
    //     sessionStorage.removeItem('orderOpenID');
    // });

    React.useEffect(() => {

        if(UserSession !== null) {

            // if (!sessionStorage.getItem('appOpened')) {
            if(1 < 2) {
                const startParam = getTelegramStartParam();

                if(startParam !== undefined && startParam.includes('orderid') !== false) {

                } else {
                    sessionStorage.removeItem('orderOpenID');
                }

                if(startParam !== undefined) {
                    const params = startParam.split('--');

                    var orderIdOpen = false;

                    const statisticpageOpen = sessionStorage.getItem('statisticpageOpened');
                    const orderOpenID = sessionStorage.getItem('orderOpenID');

                    params.some(param => {
                        const match = param.match(/tariffsPage/);
                        if (match) {

                            navigate(`/adminpanel/${UserSession.shopid}/tariffs`)

                            return true;
                        }
                        return false;
                    });

                    params.some(param => {
                        const match = param.match(/statisticpage(\d+)/);
                        if (match && statisticpageOpen == undefined) {
                            sessionStorage.setItem('statisticpageOpened', 'true');
                            navigate(`/adminpanel/${UserSession.shopid}/statisticCharts`);
                            return true;
                        }
                        return false;
                    });

                    params.some(param => {
                        const match = param.match(/orderid(\d+)/);
                        if (match && (orderOpenID === undefined || orderOpenID === null)) {
                            orderIdOpen = 'orderid' + match[1];
                            sessionStorage.setItem('orderOpenID', match[1]);
                            return true;
                        }
                        return false;
                    });

                    params.some(param => {
                        const match = param.match(/orderidadmin(\d+)/);
                        if (match) {
                            sessionStorage.setItem('orderAdminOpenID', match[1]);
                            navigate(`/adminpanel/${UserSession.shopid}/showOrder/${match[1]}`);
                            return true;
                        }
                        return false;
                    });
                }
                sessionStorage.setItem('appOpened', 'true');
            }
        }
    }, [UserSession, setParameter]);

    React.useEffect(() => {
        if (UserSession === null || requestedItemID === false) {
            return;
        }

        if (productStartOpenedRef.current === requestedItemID) {
            return;
        }

        productStartOpenedRef.current = requestedItemID;
        let isCancelled = false;

        const fetchGood = async (goodID) => {
            setItem(false);
            setItemOpenState('pending');
            setOpenGoodModal(true);
            setOpenGoodDialog(true);

            try {
                const response = await getGood(goodID);
                const good = response && response.good ? response.good.item : null;

                if (isCancelled) {
                    return;
                }

                if (good) {
                    setItem(good);
                    setItemOpenState('ready');
                } else {
                    setItemOpenState('error');
                    setOpenGoodModal(false);
                }
            } catch (error) {
                if (!isCancelled) {
                    setItemOpenState('error');
                    setOpenGoodModal(false);
                }
            }

            sessionStorage.setItem('appOpened', 'true');
        };

        fetchGood(requestedItemID);

        return () => {
            isCancelled = true;
        };
    }, [UserSession, requestedItemID]);

    React.useEffect(() => {
        if (UserSession === null || cartStartHydratedRef.current || requestedCartItems.length < 1) {
            return;
        }

        cartStartHydratedRef.current = true;
        let isCancelled = false;

        const withRequestTimeout = (promise, timeout = 4000) => (
            Promise.race([
                promise,
                new Promise(resolve => {
                    window.setTimeout(() => resolve({ success: 0, timeout: true }), timeout);
                })
            ])
        );

        const hydrateCart = async () => {
            setOpenGoodModal(false);
            setOpenBasket(true);
            setCartOpenState('ready');
            setCartHydrationState('loading');

            try {
                await Promise.allSettled(
                    requestedCartItems.map(async cartItem => {
                        const addResult = await withRequestTimeout(
                            AddItemBasketByIDAction(cartItem.itemID, cartItem.size || '0', false),
                            4000
                        );

                        const basketID = addResult && addResult.data ? addResult.data.basketId : false;
                        if (!basketID) {
                            return addResult;
                        }

                        return withRequestTimeout(
                            SetBasketItemCountAction(basketID, cartItem.count || 1, false),
                            4000
                        );
                    })
                );

                if (isCancelled) {
                    return;
                }

                const basketResult = await withRequestTimeout(getBasket().catch(() => null), 4000);

                if (!isCancelled) {
                    setCartHydrationState(basketResult && basketResult.timeout ? 'error' : 'done');
                }
            } catch (error) {
                if (!isCancelled) {
                    setOpenBasket(true);
                    setCartOpenState('error');
                    setCartHydrationState('error');
                }
            }
        };

        hydrateCart();

        return () => {
            isCancelled = true;
        };
    }, [UserSession, requestedCartItems, getBasket, AddItemBasketByIDAction, SetBasketItemCountAction]);

    const [openGoodDialog, setOpenGoodDialog] = React.useState(initialItemID !== false);
    const handleCloseGoodDialog = () => {
        setOpenGoodDialog(false);
        setOpenGoodModal(false);
        setRequestedItemID(false);
        setItemOpenState('idle');
    };

    if(errorAuth !== false) {
        return (
            <>
                <p className={"text-center p-4 mt-3"}>
                    Error with Auth:<br/>
                    {errorAuth}
                </p>
            </>
        )
    }

    if(UserSession === null || ShopSession.settings === undefined) {
        return requestedCartItems.length > 0
            ? <CartBootstrapShell label={t('loading...')} />
            : requestedItemID !== false
            ? <ProductBootstrapShell label={t('loading...')} />
            : <ShopBootstrapShell />;
    }

    return (
        <>
            {
                ShopSession.settings.blocked > 0 ? (
                    <div className="w-100 mt-4 text-center">
                        <img src={BlockImg} alt="block img" width={200} />
                        <h4><b>{t('shop_blocked')}</b></h4>
                        <p><b>ID #{shopid}</b></p>
                        <p>{t('to_unlock_write_to_the_store_administrator')} <a className="channel-link" href={`https://t.me/${ShopSession.settings.usernameAdmin}`}>{ShopSession.settings.usernameAdmin}</a></p>
                        <hr/>
                        <PoweredByWebappz />
                    </div>
                ) : (
                    <>
                        {
                            cartOpenState === 'pending' ? (
                                <CartBootstrapShell label={t('loading...')} />
                            ) : itemOpenState === 'pending' ? (
                                <ProductBootstrapShell label={t('loading...')} />
                            ) : (
                                <ShopRouter {...modalHandlers} />
                            )
                        }
                        {
                            item && (
                                <React.Suspense fallback={null}>
                                    <GoodDialog
                                        open={openGoodDialog}
                                        setOpen={setOpenGoodDialog}
                                        handleClickOpen={() => setOpenGoodDialog(true)}
                                        handleClose={handleCloseGoodDialog}
                                        good={item}
                                        setLoading={() => {
                                        }}
                                        setOpenGoodModalLocal={(state) => {
                                            setOpenGoodDialog(state);
                                            setOpenGoodModal(state);
                                        }}
                                        from='card'
                                        hideButton={true}
                                    />
                                </React.Suspense>
                            )
                        }
                    </>
                )
            }
        </>
    );
};

export default ShopLayout;
